/**
 * Sticky header: scroll states, body offset, mobile drawer.
 */
import {
	SCROLL_LOCK_BODY_CLASS,
	SCROLL_UNLOCK_EVENT,
} from '../shared/background-scroll-lock';

( function () {
	'use strict';

	const SCROLL_THRESHOLD = 60;
	const SCROLLED_CLASS = 'is-scrolled';
	const DRAWER_OPEN_CLASS = 'is-drawer-open';
	const BODY_SCROLL_LOCK_CLASS = 'alteia-header-drawer-open';

	/**
	 * Measure header layers and set CSS variables / body offset.
	 *
	 * @param {HTMLElement} header Header section element.
	 */
	function setBodyOffset( header ) {
		const headerType = header.dataset.headerType || 'group';
		const isTransparent = header.dataset.transparent === 'true';
		const mainEl =
			header.querySelector( '.alteia-header__main' ) ||
			header.querySelector( '.alteia-header__inner' );
		const navEl = header.querySelector( '.alteia-header__nav' );
		const totalHeight = header.offsetHeight;
		const topBarEl = header.querySelector( '.alteia-header__top-bar' );
		const topBarHeight = topBarEl ? topBarEl.offsetHeight : 0;
		const mainHeight = mainEl ? mainEl.offsetHeight : totalHeight;
		const opaqueHeight = topBarHeight + mainHeight;
		let paddingTop = totalHeight;

		if ( isTransparent && 'group' === headerType ) {
			// Transparent group header overlays the hero — no body offset.
			paddingTop = 0;
		} else if ( isTransparent && 'child' === headerType ) {
			// Child sites: only the solid top bar + main header reserve space.
			paddingTop = opaqueHeight;
		}

		document.body.style.setProperty(
			'--alteia-header-height',
			`${ totalHeight }px`
		);
		document.body.style.setProperty(
			'--alteia-header-main-height',
			`${ opaqueHeight }px`
		);
		document.body.style.setProperty(
			'--alteia-header-nav-height',
			navEl ? `${ navEl.offsetHeight }px` : '0px'
		);
		document.body.style.paddingTop =
			paddingTop > 0 ? `${ paddingTop }px` : '0';
	}

	/**
	 * Swap logo image when a single-src logo is used (child header).
	 * Group headers render both variants and switch via CSS / `.is-scrolled`.
	 *
	 * @param {HTMLElement} header   Header element.
	 * @param {boolean}     scrolled Whether scrolled state is active.
	 */
	function swapLogo( header, scrolled ) {
		if (
			header.querySelector( '.alteia-header__logo-image--light' ) &&
			header.querySelector( '.alteia-header__logo-image--dark' )
		) {
			return;
		}

		const logoImg = header.querySelector(
			'.alteia-header__logo img, .wp-block-site-logo img'
		);
		const logoDefault = header.dataset.logoDefault || '';
		const logoScrolled = header.dataset.logoScrolled || '';
		const headerType = header.dataset.headerType || 'group';

		if ( ! logoImg || ! logoDefault ) {
			return;
		}

		if ( 'child' === headerType ) {
			logoImg.src = logoDefault;
			return;
		}

		if ( logoScrolled ) {
			logoImg.src = scrolled ? logoScrolled : logoDefault;
		}
	}

	/**
	 * Initialize mobile drawer for child headers.
	 *
	 * @param {HTMLElement} header Header element.
	 */
	function initDrawer( header ) {
		const toggle = header.querySelector( '.alteia-header__menu-toggle' );
		const drawer = header.querySelector( '.alteia-header__drawer' );
		const closeBtn = header.querySelector( '.alteia-header__drawer-close' );
		const backdrop = header.querySelector(
			'.alteia-header__drawer-backdrop'
		);

		if ( ! toggle || ! drawer ) {
			return;
		}

		/**
		 * @param {boolean} open Whether drawer should open.
		 */
		function setDrawerOpen( open ) {
			header.classList.toggle( DRAWER_OPEN_CLASS, open );
			document.body.classList.toggle( BODY_SCROLL_LOCK_CLASS, open );
			toggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
			const labelOpen = toggle.getAttribute( 'data-label-open' ) || 'Open menu';
			const labelClose =
				toggle.getAttribute( 'data-label-close' ) || 'Close menu';
			toggle.setAttribute( 'aria-label', open ? labelClose : labelOpen );
			drawer.setAttribute( 'aria-hidden', open ? 'false' : 'true' );

			if ( backdrop ) {
				backdrop.hidden = ! open;
			}

			if ( open ) {
				closeBtn?.focus();
			} else {
				toggle.focus();
			}
		}

		toggle.addEventListener( 'click', () => {
			const isOpen = header.classList.contains( DRAWER_OPEN_CLASS );
			setDrawerOpen( ! isOpen );
		} );

		closeBtn?.addEventListener( 'click', () => setDrawerOpen( false ) );
		backdrop?.addEventListener( 'click', () => setDrawerOpen( false ) );

		drawer.querySelectorAll( 'a' ).forEach( ( link ) => {
			link.addEventListener( 'click', () => setDrawerOpen( false ) );
		} );

		document.addEventListener( 'keydown', ( event ) => {
			if (
				'Escape' === event.key &&
				header.classList.contains( DRAWER_OPEN_CLASS )
			) {
				setDrawerOpen( false );
			}
		} );
	}

	/**
	 * @param {HTMLElement} header Header section element.
	 */
	function initHeader( header ) {
		const scrollNav = header.dataset.scrollNav === 'true';
		const headerType = header.dataset.headerType || 'group';
		const forceScrolled = header.dataset.startScrolled === 'true';
		const isTransparent = header.dataset.transparent === 'true';
		let ticking = false;
		let lastScrolled = null;

		header.classList.add( 'is-sticky' );
		setBodyOffset( header );
		initDrawer( header );

		/**
		 * Apply scrolled state.
		 */
		function applyScrollState() {
			if ( document.body.classList.contains( SCROLL_LOCK_BODY_CLASS ) ) {
				return;
			}

			const scrolled =
				forceScrolled || window.scrollY >= SCROLL_THRESHOLD;

			if ( scrolled === lastScrolled && null !== lastScrolled ) {
				return;
			}

			lastScrolled = scrolled;

			if ( 'group' === headerType ) {
				header.classList.toggle( SCROLLED_CLASS, scrolled );
				document.body.classList.toggle(
					'alteia-header-is-scrolled',
					scrolled
				);
			} else if ( scrollNav && isTransparent ) {
				header.classList.toggle( SCROLLED_CLASS, scrolled );
				document.body.classList.toggle(
					'alteia-header-is-scrolled',
					scrolled
				);
			}

			swapLogo( header, scrolled );
			setBodyOffset( header );
		}

		/**
		 * Request animation frame scroll handler.
		 */
		function onScroll() {
			if ( ticking ) {
				return;
			}

			ticking = true;
			window.requestAnimationFrame( () => {
				applyScrollState();
				ticking = false;
			} );
		}

		/**
		 * Re-sync header state after modal closes and scroll is restored.
		 */
		function onBackgroundScrollUnlock() {
			lastScrolled = null;
			applyScrollState();
		}

		applyScrollState();
		window.addEventListener( 'scroll', onScroll, { passive: true } );
		document.addEventListener(
			SCROLL_UNLOCK_EVENT,
			onBackgroundScrollUnlock
		);
		window.addEventListener(
			'resize',
			() => {
				setBodyOffset( header );
			},
			{ passive: true }
		);
	}

	document.addEventListener( 'DOMContentLoaded', () => {
		document.querySelectorAll( '.alteia-header' ).forEach( initHeader );
	} );
} )();
